import { InteractionStatus, InteractionType } from "@azure/msal-browser";
import { MsalAuthenticationTemplate, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { ChakraProvider, CircularProgress, extendTheme, withDefaultColorScheme } from "@chakra-ui/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Dashboard } from "./components/Dashboard/Dashboard";
import { PrintGraphsPage } from "./components/PrintGraphsPage/PrintGraphsPage";
import { PrintPage } from "./components/PrintPage/PrintPage";
import { SiteColorsProvider } from "./components/SiteColorsProvider/SiteColorsProvider";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  BarController,
  LineController,
  TimeScale,
);

function App() {
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  if (!isAuthenticated && inProgress !== InteractionStatus.None) {
    return <CircularProgress isIndeterminate color="brand.400" />;
  }

  return (
    <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
      <LoggedIn />
    </MsalAuthenticationTemplate>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const theme = extendTheme(
  {
    colors: {
      brand: {
        50: "#fff3dd",
        100: "#fcdeb4",
        200: "#f5c887",
        300: "#f0b25a",
        400: "#ec9c2d",
        500: "#d28313",
        600: "#a4660c",
        700: "#764907",
        800: "#482b00",
        900: "#1c0e00",
      },
      darkBrown: {
        50: "#fbf1e7",
        100: "#e3d9cd",
        200: "#cdbfb1",
        300: "#b9a693",
        400: "#a58c75",
        500: "#8b735b",
        600: "#6c5946",
        700: "#4e4032",
        800: "#30261c",
        900: "#150c00",
      },
      peach: {
        50: "#fff4e2",
        100: "#fcddb6",
        200: "#f8c688",
        300: "#f5b058",
        400: "#f39a2b",
        500: "#da8016",
        600: "#aa6410",
        700: "#79470a",
        800: "#492a03",
        900: "#1b0d00",
      },
    },
    components: {
      Input: {
        defaultProps: {
          focusBorderColor: "brand.500",
        },
        variants: {
          outline: {
            field: {
              background: "white",
            },
          },
        },
      },
      Tabs: {
        baseStyle: {
          tabpanel: {
            p: 0,
          },
        },
      },
    },
  },
  withDefaultColorScheme({ colorScheme: "brand" }),
);

const LoggedIn = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ChakraProvider theme={theme}>
        <SiteColorsProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/print/:id" element={<PrintPage />} />
              <Route path="/printGraphs" element={<PrintGraphsPage />} />
            </Routes>
          </BrowserRouter>
        </SiteColorsProvider>
      </ChakraProvider>
    </QueryClientProvider>
  );
};

export default App;
